import './assets/css/common.scss';
import 'bootstrap/dist/js/bootstrap.js';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
  RequireAuth,
  NavigateConst,
  PROXY_EXPIRED_URL,
  PROXY_REGISTER_URL,
  PROXY_AUTHORIZATION_ERROR_URL,
  AUTH_SUCCESS_URL,
  AuthProvider,
  domainForSegment,
} from 'common';
import {
  ProxyRegister,
  ProxyAuthorizationError,
  ProxyExpiredForm,
} from 'templates';
import {
  ForgotUsername,
  ForgotPassword,
  Form,
  DrRegistrationForm,
  SignInForm,
  HomePage,
  IAMAuthSuccess,
  MaintenancePage,
} from 'pages';
import { useContext, useEffect, useMemo, useRef } from 'react';
import { GlobalContext } from './context';
import { DrAuthorizationChooser } from 'pages/authorization/DrAuthorizationChooser';
import { startOpenReplayRecording, setOpenReplayAssistEnabled, setOpenReplayCoBrowsingEnabled } from 'common/openreplay/openreplay';
import CoBrowseIndicator from 'common/openreplay/CoBrowseIndicator';
import { AppWrapper } from 'common/AppWrapper';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeRoutesConfig } from 'common/AdobeConfigRoutes';
import { Logger, Monitoring } from '@sentara-health/platform-blueprint';
import { useAzureLogger, AzureMonitoring } from '@sentara-health/platform-logging-monitoring';
import { isAvMed } from 'context';
import { AppLayout } from 'common/AppLayout';
const App = ({
  header,
  authToken,
  xAuthToken,
  xSentaraTokenID,
}: {
  header: Record<string, string | undefined>;
  authToken: string;
  xAuthToken: string;
  xSentaraTokenID: string;
}) => {
  const { setUserEncodedData, getFeature, setLogging, setMonitoring } =
    useContext(GlobalContext);
  const prevUrl = useRef('');
  const { pathname } = useLocation();
  const { pageView } = useAdobeAnalytics();
  const navigate = useNavigate();
  if (pathname) {
    if (getFeature?.MaintenancePage && pathname !== '/maintenace') {
      navigate('/maintenace');
    } else if (!getFeature?.MaintenancePage && pathname === '/maintenace') {
      navigate(NavigateConst.signIn);
    }
  }

  const appinsightsEnabled = getFeature?.AzureAppInsights;

  const loggerHook = useAzureLogger({
    applicationName: 'Member Portal',
    enabledLogLevel: [
      'event',
      'error',
      'warn',
      'info',
      'debug',
      'trace',
      'api',
    ],
    environment: process.env.REACT_APP_ENVIRONMENT ?? 'QA',
    getUserId: () => '',
  });

  const logger: Logger | null = useMemo(() => {
    return appinsightsEnabled ? loggerHook : null;
  }, [appinsightsEnabled]);

  const monitoring: Monitoring | null = useMemo(() => {
    return appinsightsEnabled && logger
      ? new AzureMonitoring(
          process.env.REACT_APP_INSTRUMENTATION_KEY ?? '',
          logger,
          () => ''
        )
      : null;
  }, [appinsightsEnabled, logger]);

  useEffect(() => {
    if (appinsightsEnabled) {
      setLogging(logger);
      setMonitoring(monitoring);
    } else {
      setLogging(null);
      setMonitoring(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appinsightsEnabled, logger, monitoring]);

  const injectAdobeScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      const scriptId = 'adobe-launch-script';

      // Check if the script is already injected
      if (document.getElementById(scriptId)) {
        console.log('[Adobe Analytics] Script already loaded');
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = process.env.REACT_APP_ADOBE_URL ?? '';
      script.integrity = process.env.REACT_APP_ADOBE_INTEGRITY ?? '';
      script.crossOrigin = 'anonymous';
      script.id = scriptId;
      script.async = true;

      script.onload = () => {
        console.log('[Adobe Analytics] Script loaded successfully');
        resolve();
      };

      script.onerror = () => {
        console.error('[Adobe Analytics] Failed to load script');
        reject(new Error('Failed to load Adobe Analytics script'));
      };

      document.head.appendChild(script);
    });
  };

  useEffect(() => {
    const func = async () => {
      // set anynomous user LD flags before login.
      setUserEncodedData(
        JSON.stringify({
          key: 'anonymous',
          platform: 'web',
        })
      );
    };
    func();
  }, [setUserEncodedData]);

  useEffect(() => {
    const func = async () => {
      setOpenReplayAssistEnabled(!!getFeature?.OpenReplayAssist);
      setOpenReplayCoBrowsingEnabled(!!getFeature?.OpenReplayCoBrowsing);
      if (getFeature?.OpenReplayAssist) {
        void startOpenReplayRecording();
      }
    };
    func();
  }, [getFeature?.OpenReplayAssist, getFeature?.OpenReplayCoBrowsing]);

  //  create useEffect that runs on URL change and based on condition calls pageView function from useAdobeAnalytics
  useEffect(() => {
    if (pathname !== prevUrl.current) {
      prevUrl.current = pathname;
      const adobeRouteConfig =
        AdobeRoutesConfig?.[pathname as keyof typeof AdobeRoutesConfig];
      if (adobeRouteConfig) {
        pageView({
          segment: `${domainForSegment}${pathname}`,
          page: adobeRouteConfig,
        });
      }
    }
  }, [pathname]);

  useEffect(() => {
    const loadAdobeScript = async () => {
      if (getFeature?.AdobeAnalytics) {
        try {
          await injectAdobeScript();
        } catch (error) {
          console.error('[Adobe Analytics] Script injection failed:', error);
        }
      }
    };

    loadAdobeScript();
  }, [getFeature?.AdobeAnalytics]);

  return (
    <AppWrapper
      header={header}
      authToken={authToken}
      xAuthToken={xAuthToken}
      xSentaraTokenID={xSentaraTokenID}
    >
      <CoBrowseIndicator />
      <AuthProvider>
        <Routes>
          <Route element={<AppLayout />}>
          <Route
            path={NavigateConst.signIn}
            element={
              getFeature?.MaintenancePage ? <MaintenancePage /> : <SignInForm />
            }
          />
        
          <Route
            path="/reset-password"
            element={
              getFeature?.MaintenancePage ? (
                <MaintenancePage />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route
            path="/login/forgot-username"
            element={
              getFeature?.MaintenancePage ? (
                <MaintenancePage />
              ) : (
                <ForgotUsername />
              )
            }
          />
          <Route path={NavigateConst.registration} element={<Form />} />
          <Route path={NavigateConst.drRegistration} element={<DrRegistrationForm />} />
          {isAvMed() && (
            <Route path={NavigateConst.registrationProxy} element={<Form />} />
            )}
          {getFeature?.DesignatedRepresentativeTab && (
            <Route path={NavigateConst.childOnlyPlanDesignatedRep} element={<DrRegistrationForm />} />
          )}
          {getFeature?.DesignatedRepresentativeTab && (
            <Route path={NavigateConst.drAuthorizationChooser} element={<DrAuthorizationChooser />} />
          )}
          <Route path="/maintenace" element={<MaintenancePage />} />
          <Route
            path={PROXY_EXPIRED_URL}
            element={<ProxyExpiredForm />}
          />
          <Route path={PROXY_REGISTER_URL} element={<ProxyRegister />} />
          <Route
            path={PROXY_AUTHORIZATION_ERROR_URL}
            element={<ProxyAuthorizationError />}
          />
          <Route path={AUTH_SUCCESS_URL} element={<IAMAuthSuccess />} />
          <Route
            path="/home"
            element={
              getFeature?.MaintenancePage ? (
                <MaintenancePage />
              ) : (
                <RequireAuth>
                  <HomePage />
                </RequireAuth>
              )
            }
          />
             <Route
            path="*"
            element={
              getFeature?.MaintenancePage ? (
                <MaintenancePage />
              ) : (
                <RequireAuth>
                  <HomePage />
                </RequireAuth>
              )
            }
          />
        </Route>
        </Routes>
      </AuthProvider>
    </AppWrapper>
  );
};

export default App;
